<script setup>
import { ref, computed, watch } from 'vue';
import { useAssistenciaTecnicaStore } from '@/stores/assistenciaTecnica';

const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  assistencia: {
    type: Object,
    default: null
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:open', 'save', 'cancel']);

// Reactive form data
const formData = ref({
  complemento_entrega: '',
  numero_entrega: '',
  dtprevisao: '',
  horaprevisao: ''
});

// Watch for changes in the opened modal or assistencia data
watch(() => [props.open, props.assistencia], () => {
  if (props.open && props.assistencia) {
    // Initialize form with current assistencia data
    const assistencia = props.assistencia;
    
    // Parse the dtprevisao to separate date and time
    let dataPrev = '';
    let horaPrev = '';
    
    if (assistencia.dtprevisao) {
      try {
        const dtObj = new Date(assistencia.dtprevisao);
        if (dtObj instanceof Date && !isNaN(dtObj)) {
          // Format date as YYYY-MM-DD
          dataPrev = dtObj.toISOString().split('T')[0];
          
          // Format time as HH:MM
          const hours = dtObj.getHours().toString().padStart(2, '0');
          const minutes = dtObj.getMinutes().toString().padStart(2, '0');
          horaPrev = `${hours}:${minutes}`;
        }
      } catch (err) {
        console.error('Error parsing date:', err);
      }
    }
    
    formData.value = {
      complemento_entrega: assistencia.complemento_entrega || '',
      numero_entrega: assistencia.numero_entrega || '',
      dtprevisao: dataPrev,
      horaprevisao: horaPrev
    };
  }
}, { immediate: true });

// Close the modal
const handleCancel = () => {
  emit('update:open', false);
  emit('cancel');
};

// Save the form data
const handleSave = () => {
  // Combine date and time for dtprevisao
  const dateTime = formData.value.dtprevisao && formData.value.horaprevisao
    ? `${formData.value.dtprevisao} ${formData.value.horaprevisao}:00`
    : null;
  
  const dataToSave = {
    complemento_entrega: formData.value.complemento_entrega,
    numero_entrega: formData.value.numero_entrega ? parseInt(formData.value.numero_entrega) : null,
    dtprevisao: dateTime
  };
  
  emit('save', dataToSave);
};
</script>

<template>
  <v-dialog
    :model-value="open"
    @update:model-value="emit('update:open', $event)"
    max-width="600"
    persistent
  >
    <v-card>
      <v-card-title class="text-h5 bg-primary text-white">
        <v-icon start icon="mdi-pencil" class="mr-2"></v-icon>
        Editar Informações da Assistência Técnica #{{ assistencia?.id }}
      </v-card-title>
      
      <v-card-text class="pt-4">
        <v-form @submit.prevent="handleSave">
          <v-row>
            <!-- Data/Hora Previsão -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.dtprevisao"
                label="Data Previsão"
                type="date"
                density="compact"
                variant="outlined"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.horaprevisao"
                label="Hora Previsão"
                type="time"
                density="compact"
                variant="outlined"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            
            <!-- Endereço -->
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.numero_entrega"
                label="Número"
                type="text"
                density="compact"
                variant="outlined"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="formData.complemento_entrega"
                label="Complemento"
                type="text"
                density="compact"
                variant="outlined"
                hide-details="auto"
                class="mb-3"
              ></v-text-field>
            </v-col>
          </v-row>
          
          <div class="d-flex justify-end mt-4">
            <v-btn 
              color="grey-darken-1" 
              variant="text" 
              class="mr-2" 
              @click="handleCancel"
              :disabled="loading"
            >
              Cancelar
            </v-btn>
            <v-btn 
              color="primary" 
              type="submit"
              :loading="loading"
              :disabled="loading"
            >
              Salvar
            </v-btn>
          </div>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
